import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import BrandChip from '../components/BrandChip';
import Breadcrumbs from '../components/Breadcrumbs';
import Footer from '../components/Footer';
import WhatsAppWidget from '../components/WhatsAppWidget';
import { 
  CheckCircle, 
  ChevronDown, 
  ChevronUp, 
  Phone, 
  Mail, 
  ArrowRight,
  Home,
  Building2,
  Sparkles,
  Zap
} from 'lucide-react';

const FloorCleaningRobotsPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const products = [
    {
      name: "Nexora AI Floor Robot",
      slug: "nexora",
      price: "₹1,49,990",
      originalPrice: "₹1,79,990",
      image: "https://customer-assets.emergentagent.com/job_smart-living-18/artifacts/0tnzn2dv_0c190456-b2a5-49d2-bde7-70d444461d9a-md.jpeg",
      features: ["5200Pa hyper suction", "AI navigation", "Self-emptying dock", "7-week dust capacity"],
      badge: "Premium Choice"
    },
    {
      name: "LumineX 300",
      slug: "luminex-300",
      price: "₹29,990",
      originalPrice: "₹34,990",
      image: "https://customer-assets-jt897jd0.emergentagent.net/job_582314cb-e0d4-4f3b-95a4-147554a5a708/artifacts/unyfq400_8.%20LumineX%20300.png",
      features: ["Up to 15,000Pa suction", "LiDAR + AI navigation", "3L self-empty dock (70 days)", "Vacuum + mop together"],
      badge: "Best Value"
    },
    {
      name: "HygienX 300",
      slug: "hygienx-300",
      price: "₹79,990",
      originalPrice: "₹89,990",
      image: "https://customer-assets.emergentagent.com/job_80fffe0f-7ad4-44ce-aa36-08a4bccc4edb/artifacts/4ik16avb_PHOTO-2026-05-30-18-18-13.jpg",
      features: ["15,000Pa suction", "Auto mop washing + hot-air drying", "RT²R 4.0 LiDAR mapping", "Self-cleaning dock"],
      badge: "Fully Hands-Free"
    },
    {
      name: "Otto A1 Pro",
      slug: "otto-a1-pro",
      price: "₹19,990",
      originalPrice: "₹29,990",
      image: "https://customer-assets.emergentagent.com/job_80fffe0f-7ad4-44ce-aa36-08a4bccc4edb/artifacts/dr6yr5fe_cdbfadfb-c683-4a0a-b93b-ab8a2fb07143.png",
      features: ["3-in-1 cleaning", "Electrolytic mopping", "Smart navigation", "Budget-friendly"],
      badge: "Best Seller"
    },
    {
      name: "Escargot",
      slug: "escargot-commercial",
      price: "₹23,99,900",
      originalPrice: "",
      image: "https://customer-assets.emergentagent.com/job_80fffe0f-7ad4-44ce-aa36-08a4bccc4edb/artifacts/78savfvp_jan.png",
      features: ["Commercial 3-in-1 sweep + vacuum + mop", "40,000 m² smart mapping", "UV disinfection lamp", "500 m²/h efficiency"],
      badge: "Commercial"
    }
  ];

  const benefits = [
    {
      icon: Zap,
      title: "Powerful Suction",
      description: "Up to 5200Pa suction power handles pet hair, dust, and debris on all floor types including marble, tiles, and carpets."
    },
    {
      icon: Sparkles,
      title: "Smart Mopping",
      description: "Intelligent water control and electrolytic mopping technology for streak-free, sanitized floors without manual intervention."
    },
    {
      icon: Home,
      title: "Designed for Indian Homes",
      description: "Handles Indian dust conditions, transitions smoothly between marble and tiles, and navigates around traditional furniture."
    },
    {
      icon: Building2,
      title: "Office Ready",
      description: "Large coverage area and programmable schedules make our robots perfect for offices, clinics, and commercial spaces."
    }
  ];

  const faqs = [
    {
      question: "What is the best robot vacuum cleaner for Indian homes?",
      answer: "For Indian homes in 2026, we recommend the Harpalyk LumineX 300 (₹29,990) for the best balance of power and value — up to 15,000Pa suction, LiDAR + AI navigation and a 3L self-emptying dock. Choose the Nexora AI (₹1,49,990) for premium hands-free features or the Otto A1 Pro (₹19,990) for budget-conscious buyers. All handle Indian dust conditions, marble/tile floors, and pet hair effectively."
    },
    {
      question: "Can robot vacuums clean Indian-style marble and tile floors?",
      answer: "Yes, Harpalyk robot vacuums are specifically designed for Indian floor types. They feature rubberized brushes that won't scratch marble, adjustable suction for different surfaces, and intelligent sensors to navigate thresholds between rooms. The mopping function works safely on all sealed hard floors."
    },
    {
      question: "How long does a robot vacuum battery last?",
      answer: "Harpalyk robot vacuums have battery life ranging from 90 minutes (Otto A1 Pro) to 180 minutes (Nexora AI). Most robots automatically return to their charging dock when battery is low and resume cleaning after recharging. For larger homes, we recommend models with auto-resume functionality."
    },
    {
      question: "Do robot vacuums work with pet hair?",
      answer: "Absolutely! Harpalyk robots feature anti-tangle brush systems and powerful suction specifically designed for pet hair. The Nexora AI and LumineX 300 models have HEPA filtration that captures 99.97% of allergens, making them ideal for homes with pets or allergy sufferers."
    },
    {
      question: "How much does a robot vacuum cost in India?",
      answer: "Robot vacuum cleaners in India cost between ₹19,990 and ₹1,49,990 depending on features. Entry models like the Otto A1 Pro start at ₹19,990, the LumineX 300 with LiDAR and self-emptying dock costs ₹29,990, and premium self-cleaning models like the HygienX 300 cost ₹79,990. EMI options start around ₹1,666/month."
    },
    {
      question: "Are robot vacuums worth buying in India?",
      answer: "Yes. A robot vacuum cleans daily without supervision, which manual cleaning rarely matches. Compared with typical domestic help costs of ₹2,000-4,000/month for sweeping and mopping, most robot vacuums pay for themselves within 8-15 months while delivering more consistent results."
    },
    {
      question: "What maintenance do robot vacuums require?",
      answer: "Minimal maintenance is needed: empty the dustbin weekly (or let self-emptying models handle it), clean the brushes monthly, and wipe sensors occasionally. Harpalyk provides 2-year warranty and pan-India service support with genuine replacement parts readily available."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
    }))
  };

  return (
    <>
      <Helmet>
        <title>SMARTBOT — Intelligent Floor Cleaning Robots | Harpalyk India</title>
        <meta name="description" content="Compare India's best robot vacuum & mop cleaners — LiDAR navigation, up to 15,000Pa suction, self-emptying docks. From ₹19,990 with free delivery, EMI & 2-year warranty." />
        <meta name="keywords" content="robot vacuum cleaner india, floor cleaning robot, automatic vacuum cleaner, robotic floor cleaner, best robot vacuum india, smart home cleaning robot" />
        <link rel="canonical" href="https://harpalyk.com/floor-cleaning-robots" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <Header />

      <main className="pt-20">
        <div className="h-1 bg-gradient-to-r from-emerald-500 via-emerald-400 to-transparent" data-testid="family-accent-bar-smartbot" aria-hidden="true" />
        <Breadcrumbs items={[{ label: 'Floor Cleaning Robots' }]} />
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-emerald-900 via-emerald-800 to-teal-900 text-white py-20 px-6 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block bg-emerald-500/20 text-emerald-300 px-4 py-2 rounded-full text-sm font-medium mb-6">
                  HARPALYK · SMARTBOT FAMILY
                </span>
                <h1 className="text-4xl lg:text-5xl font-bold leading-tight mb-6">
                  SMARTBOT — Intelligent Floor Cleaning Robots
                </h1>
                <p className="text-xl text-emerald-100 mb-8 leading-relaxed">
                  AI-powered <strong>floor cleaning robots</strong> that vacuum, mop, and sanitize your floors automatically. 
                  Designed specifically for Indian dust conditions, marble floors, and busy lifestyles.
                </p>
                <div className="flex flex-wrap gap-4 mb-8">
                  <a href="#products" className="bg-emerald-500 hover:bg-emerald-400 text-white px-8 py-4 rounded-lg font-semibold flex items-center gap-2 transition-all">
                    Shop Floor Robots <ArrowRight className="w-5 h-5" />
                  </a>
                  <a href="tel:+917011370322" className="border-2 border-white/30 hover:border-white text-white px-8 py-4 rounded-lg font-semibold flex items-center gap-2 transition-all">
                    <Phone className="w-5 h-5" /> Free Demo
                  </a>
                </div>
                
                <div className="flex flex-wrap gap-6 pt-8 border-t border-white/20">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-emerald-400">5,000+</p>
                    <p className="text-sm text-emerald-200">Happy Customers</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-emerald-400">₹19,990</p>
                    <p className="text-sm text-emerald-200">Starting Price</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-emerald-400">4.8/5</p>
                    <p className="text-sm text-emerald-200">Customer Rating</p>
                  </div>
                </div>
              </div>
              
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80" 
                  alt="Robot Vacuum Cleaner India" 
                  className="rounded-2xl shadow-2xl"
                />
                <div className="absolute -bottom-4 -right-4 bg-white text-emerald-900 px-6 py-3 rounded-xl shadow-lg">
                  <p className="font-bold">Starting ₹19,990</p>
                  <p className="text-sm text-gray-600">EMI from ₹1,666/mo</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-20 px-6 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-emerald-600 font-semibold uppercase tracking-wide text-sm">Why Harpalyk</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mt-2 mb-4">
                Why Choose Harpalyk Floor Cleaning Robots?
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                  <div className="flex items-start gap-4">
                    <div className="bg-emerald-100 p-4 rounded-xl">
                      <benefit.icon className="w-8 h-8 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                      <p className="text-gray-600">{benefit.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Products */}
        <section id="products" className="py-20 px-6 lg:px-12 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-emerald-600 font-semibold uppercase tracking-wide text-sm">Our Products</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mt-2 mb-4">
                Best Robot Vacuum Cleaners in India
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                From budget-friendly to premium, find the perfect floor cleaning robot for your home.{' '}
                Not sure which to pick? Read our{' '}
                <a href="/blog/best-robot-vacuum-cleaners-indian-homes-2025" className="text-emerald-600 hover:text-emerald-700 underline">
                  Best Robot Vacuum Cleaners in India (2026) comparison
                </a>.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {products.map((product, index) => (
                <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
                  <div className="relative">
                    <img src={product.image} alt={`Harpalyk SMARTBOT ${product.name} — intelligent floor cleaning robot`} className="w-full h-56 object-cover" />
                    <span className="absolute top-4 left-4 bg-emerald-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                      {product.badge}
                    </span>
                    <BrandChip family="SMARTBOT" />
                  </div>
                  <div className="p-6">
                    <p className="text-[11px] font-semibold tracking-[0.18em] text-gray-400 uppercase mb-1">HARPALYK · SMARTBOT</p>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{product.name}</h3>
                    <div className="flex items-center gap-2 mb-4">
                      <p className="text-2xl font-bold text-emerald-600">{product.price}</p>
                      {product.originalPrice && <p className="text-lg text-gray-400 line-through">{product.originalPrice}</p>}
                    </div>
                    <ul className="space-y-2 mb-6">
                      {product.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-gray-600 text-sm">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <a href={`/products/${product.slug}`} className="block text-center bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-lg font-semibold transition-all">
                      View Details
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 px-6 lg:px-12 bg-gradient-to-br from-emerald-600 to-teal-700 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Ready for Effortless Floor Cleaning?
            </h2>
            <p className="text-xl text-emerald-100 mb-8">
              Join 5,000+ Indian families who have upgraded to robotic floor cleaning. 
              Free delivery, easy EMI, and 2-year warranty on all products.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/#products" className="bg-white text-emerald-700 px-8 py-4 rounded-lg font-bold hover:bg-emerald-100 transition-all">
                Shop Now
              </a>
              <a href="tel:+917011370322" className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold hover:bg-white/10 transition-all flex items-center gap-2">
                <Phone className="w-5 h-5" /> +91-7011370322
              </a>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 px-6 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-emerald-600 font-semibold uppercase tracking-wide text-sm">FAQs</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mt-2 mb-4">
                Frequently Asked Questions About Robot Vacuum Cleaners
              </h2>
            </div>
            
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <button 
                    onClick={() => toggleFaq(index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50"
                  >
                    <h3 className="font-semibold text-gray-900 pr-4">{faq.question}</h3>
                    {openFaq === index ? <ChevronUp className="w-5 h-5 text-emerald-600" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-5">
                      <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppWidget />
    </>
  );
};

export default FloorCleaningRobotsPage;
